import React from 'react';

import {
    LinearProgress,
    Paper
} from '@mui/material';

import DayRangePicker from '../../components/dayRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import { isBeforeToday } from '../../utils/dateUtils';
import {
    formatDurationHours,
    formatInteger,
    percent
} from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import { CRM_DASHBOARD_MIN_DATE } from './crmDashboardData';
import { GoalProgressBar, GoalStatusChip, getGoalRemainingText, getGoalTitle } from './crmDashboardGoal';
import {
    FASE_OPTIONS,
    VENDEDOR_OPTIONS,
    buildGroupedStatusOptions
} from './crmShared';

const allFilterOption = 'all';
const sellerFilterOptions = [
    { value: allFilterOption, label: 'Todos' },
    ...VENDEDOR_OPTIONS.map((option) => ({ value: option.id, label: option.label }))
];
const phaseFilterOptions = [
    { value: allFilterOption, label: 'Todas' },
    ...FASE_OPTIONS.map((option) => ({ value: option.id, label: option.label }))
];
const statusFilterOptions = [
    { value: allFilterOption, label: 'Todos' },
    ...buildGroupedStatusOptions()
];

const MobileFilters = ({
    canEditSellerFilter,
    dateRange,
    draftFilters,
    onDateRangeApply,
    onDateRangeReset,
    onFilterClose,
    onMultiSelectDraftChange
}) => (
    <div className='crm-mobile-dashboard-filters'>
        <div className='crm-mobile-dashboard-range-field'>
            <span className='crm-mobile-dashboard-range-label'>Período</span>
            <DayRangePicker
                ariaLabel='Filtrar dashboard por período'
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                minDate={CRM_DASHBOARD_MIN_DATE}
                maxDate={new Date()}
                onApply={onDateRangeApply}
                onClear={onDateRangeReset}
            />
        </div>

        <MultiSelectFilter
            className='crm-dashboard-filter-control crm-mobile-dashboard-seller-field'
            labelId='mobile-vendedor-filter-label'
            label='Vendedor'
            value={draftFilters.vendedor_id}
            options={sellerFilterOptions}
            allOptionValue={allFilterOption}
            disabled={!canEditSellerFilter}
            onChange={(values) => onMultiSelectDraftChange('vendedor_id', values)}
            onClose={() => onFilterClose('vendedor_id')}
        />

        <MultiSelectFilter
            className='crm-dashboard-filter-control crm-mobile-dashboard-seller-field'
            labelId='mobile-fase-filter-label'
            label='Fase'
            value={draftFilters.fase_id}
            options={phaseFilterOptions}
            allOptionValue={allFilterOption}
            onChange={(values) => onMultiSelectDraftChange('fase_id', values)}
            onClose={() => onFilterClose('fase_id')}
        />

        <MultiSelectFilter
            className='crm-dashboard-filter-control crm-mobile-dashboard-seller-field'
            labelId='mobile-status-filter-label'
            label='Status'
            value={draftFilters.status_id}
            options={statusFilterOptions}
            allOptionValue={allFilterOption}
            onChange={(values) => onMultiSelectDraftChange('status_id', values)}
            onClose={() => onFilterClose('status_id')}
        />
    </div>
);

const FunnelStageRow = ({ item, maxTicket }) => {
    const progress = percent(item.totalTicket, maxTicket);
    const averageDurationText = formatDurationHours(item.averageHours);

    return (
        <div className='crm-mobile-dashboard-stage'>
            <div className='crm-mobile-dashboard-stage-copy'>
                <p className='crm-mobile-dashboard-stage-title'>{item.label}</p>
                <span>
                    {formatInteger(item.count)} oportunidades
                    {averageDurationText ? ` · média ${averageDurationText}` : ''}
                </span>
            </div>
            <div className='crm-mobile-dashboard-stage-value'>
                <strong>{formatCurrency(item.totalTicket)}</strong>
                <LinearProgress
                    className='crm-mobile-dashboard-stage-progress'
                    variant='determinate'
                    value={progress}
                />
            </div>
        </div>
    );
};

const GoalStat = ({ label, value, tone = '' }) => (
    <div className={`crm-mobile-dashboard-goal-stat ${tone}`}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

export const CrmDashboardMobile = ({
    canEditSellerFilter,
    dateRange,
    draftFilters,
    goal,
    isLoading,
    onDateRangeApply,
    onDateRangeReset,
    onFilterClose,
    onMultiSelectDraftChange,
    viewModel
}) => {
    const isClosedPeriod = isBeforeToday(dateRange.endDate);
    const maxFunnelTicket = Math.max(...viewModel.funnelData.map((item) => item.totalTicket), 1);
    const projectionPercent = Math.round(goal.projectionRatio * 100);

    return (
        <Paper className='crm-mobile-dashboard-shell'>
            <MobileFilters
                canEditSellerFilter={canEditSellerFilter}
                dateRange={dateRange}
                draftFilters={draftFilters}
                onDateRangeApply={onDateRangeApply}
                onDateRangeReset={onDateRangeReset}
                onFilterClose={onFilterClose}
                onMultiSelectDraftChange={onMultiSelectDraftChange}
            />

            <div className='crm-mobile-dashboard-loading-slot'>
                {isLoading && <LinearProgress className='crm-dashboard-loading crm-mobile-dashboard-loading' variant='indeterminate' />}
            </div>

            <section className='crm-mobile-dashboard-funnel-section'>
                <div className='crm-mobile-dashboard-section-header'>
                    <div>
                        <p className='crm-mobile-dashboard-kicker'>Funil de oportunidades</p>
                        <h2>{formatCurrency(viewModel.totalTicket)}</h2>
                    </div>
                    <span>{formatInteger(viewModel.totalLeads)} leads</span>
                </div>

                <div className='crm-mobile-dashboard-stage-list'>
                    {viewModel.funnelData.map((item) => (
                        <FunnelStageRow key={item.label} item={item} maxTicket={maxFunnelTicket} />
                    ))}
                </div>
            </section>

            <section className='crm-mobile-dashboard-goal-section'>
                <div className='crm-mobile-dashboard-goal-ring'>
                    <span>{isClosedPeriod ? 'Resultado' : 'Projeção'}</span>
                    <strong>{projectionPercent}%</strong>
                </div>
                <div className='crm-mobile-dashboard-goal-grid'>
                    <GoalStat label='Meta' value={formatCurrency(goal.target)} />
                    <GoalStat label='Conquistado' value={formatCurrency(goal.wonTicket)} tone='is-highlight' />
                    <GoalStat label={isClosedPeriod ? 'Não realizado' : 'A realizar'} value={formatCurrency(goal.remainingTicket)} />
                    <GoalStat label='Para bater' value={`${projectionPercent}% da meta`} tone={projectionPercent >= 100 ? 'is-positive' : 'is-warning'} />
                </div>
                <div className='crm-mobile-dashboard-goal-progress'>
                    <div className='crm-mobile-dashboard-goal-progress-header'>
                        <span>{getGoalTitle(goal, !canEditSellerFilter)}</span>
                        <GoalStatusChip className='is-mobile' status={goal.status} />
                    </div>
                    <GoalProgressBar className='is-mobile' goal={goal} />
                    <p>{getGoalRemainingText(goal)}</p>
                </div>
            </section>
        </Paper>
    );
};
